import {
  Box,
  Chip,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import globalStyle from "../../../../globalStyle";
import { handleOpenSnackBar } from "../../../../utils";
import { translateText } from "../../../../utils/translationService";
import RichTextEditor from "../../../../components/TextEditor";

const DescriptionAndSpecification = ({
  inputValues,
  setInputValues,
  inputError,
  setInputError,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState({
    description: false,
  });

  const handleChangeValues = (event) => {
    const { name, value } = event.target;
    setInputValues({ ...inputValues, [name]: value });
    setInputError({ ...inputError, [name]: "" });
  };

  const handleTranslateToEnglish = async () => {
    if (!inputValues.description_ro) {
      handleOpenSnackBar(
        "error",
        t("validation.descriptionIsRequiredInRomanian"),
      );
      return;
    }
    setIsLoading((prev) => ({ ...prev, description: true }));
    try {
      const [descriptionTranslation] = await Promise.all([
        translateText(inputValues.description_ro),
      ]);
      setInputValues((prev) => ({
        ...prev,
        description_en: descriptionTranslation,
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.convertingToEnglishError"));
    } finally {
      setIsLoading((prev) => ({ ...prev, description: false }));
    }
  };

  const handleEditorChangeEnglish = (htmlContent) => {
    setInputError((err) => ({ ...err, specification_en: false }));
    setInputValues((val) => ({ ...val, specification_en: htmlContent }));
  };

  const handleEditorChangeRomanian = (htmlContent) => {
    setInputError((err) => ({ ...err, specification_ro: false }));
    setInputValues((val) => ({ ...val, specification_ro: htmlContent }));
  };

  return (
    <Box>
      <Typography fontWeight={600} fontSize={20} my={2}>
        {t("supplierProductTab.descriptionAndSpecifications")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Label
            required
            labelText={`${t("supplierProductTab.descriptionInRomanian")}`}
            htmlFor={"product-description-ro"}
          />
          <TextInput
            id={"product-description-ro"}
            styleProps={globalStyle.textField}
            placeholder={`${t("supplierProductTab.enterDescriptionInRomanian")} `}
            required
            multiline
            rows={5}
            name={"description_ro"}
            value={inputValues.description_ro}
            onChange={handleChangeValues}
            inputError={Boolean(inputError.description_ro)}
            helperText={t(inputError.description_ro)}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            mb={"5px"}
          >
            <Label
              required
              labelText={`${t("supplierProductTab.descriptionInEnglish")}`}
              htmlFor={"product-description-en"}
              labelProps={{
                marginBottom: 0,
              }}
            />
            <Chip
              label={t("common.translateToEnglish")}
              onClick={handleTranslateToEnglish}
              loading={isLoading.description}
            />
          </Stack>

          <TextInput
            id={"product-description-en"}
            styleProps={globalStyle.textField}
            placeholder={`${t("supplierProductTab.enterDescriptionInEnglish")} `}
            required
            multiline
            rows={5}
            name={"description_en"}
            value={inputValues.description_en}
            onChange={handleChangeValues}
            inputError={Boolean(inputError.description_en)}
            helperText={t(inputError.description_en)}
          />
        </Grid>
        <Grid item xs={12}>
          <Label
            required
            labelText={`${t("supplierProductTab.specificationInRomanian")}`}
            htmlFor={"product-specification-ro"}
          />
          <RichTextEditor
            value={inputValues.specification_ro || ""}
            onChange={handleEditorChangeRomanian}
          />
          {inputError.specification_ro && (
            <FormHelperText error sx={{ marginTop: 0.3 }}>
              {t(inputError.specification_ro)}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <Label
            required
            labelText={`${t("supplierProductTab.specificationInEnglish")}`}
            htmlFor={"product-specification-en"}
          />
          <RichTextEditor
            value={inputValues.specification_en || ""}
            onChange={handleEditorChangeEnglish}
          />
          {inputError.specification_en && (
            <FormHelperText error sx={{ marginTop: 0.3 }}>
              {t(inputError.specification_en)}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DescriptionAndSpecification;
